// SPDX-FileCopyrightText: © 2025 Schneider Electric
//
// SPDX-License-Identifier: LGPL-2.1-only

import type { Importance, Tag } from "@quartzds/core";
import type { ComponentInterface } from "@stencil/core";
import { Component, h, Prop, State } from "@stencil/core";

export type Layer = "hero" | "section" | "subsection";

export type Layout = "centered" | "standard";

/**
 * @slot Content to display for the block heading's CTA slot.
 * @see https://quartz.se.com/build/components/block-heading
 */
@Component({
  tag: "qds-web-block-heading",
  shadow: true,
  styleUrl: "block-heading.css",
})
export class BlockHeading implements ComponentInterface {
  /**
   * Text to display for the description.
   */
  @Prop() public readonly description?: string;

  /**
   * Text to display for the title.
   */
  @Prop() public readonly mainTitle: string;

  /**
   * The block-heading's kicker text can be specified.
   */
  @Prop() public readonly kicker?: string;

  /**
   * The block-heading subtitle can be specified.
   */
  @Prop() public readonly subtitle?: string;

  /**
   * The heading's layer. If defined at the heading level, it will cascade to all heading items.
   */
  @Prop() public layer?: Layer = "section";

  /**
   * Layout of the content.
   */
  @Prop() public readonly layout?: Layout = "standard";

  /**
   * The heading's Importance.
   */
  @Prop() public readonly importance?: Importance = "standard";

  /**
   * The heading's inline padding.
   */
  @Prop() public paddingInline = true;

  /**
   * The heading's top padding.
   */
  @Prop() public paddingTop = true;

  /**
   * The heading's Bottom padding.
   */
  @Prop() public paddingBottom = true;

  /**
   * HTML tag to use to render the title.
   */
  @Prop() public readonly titleTag?: Extract<Tag, "h2" | "h3" | "h4"> = "h2";

  @State() private hasExtensionContent: boolean;

  public render() {
    return (
      <section
        data-layer={this.layer}
        data-importance={this.importance}
        data-layout={this.layout}
      >
        <div
          class={{
            "content-area": true,
            "qds-padding-top": this.paddingTop,
            "qds-padding-bottom": this.paddingBottom,
            "qds-padding-inline": this.paddingInline,
          }}
        >
          <div class="heading">
            <div class="title-description">
              <qds-title
                tag={this.titleTag}
                layer="main"
                level={this.layer === "subsection" ? "subsection" : "section"}
                variant={
                  this.layer === "hero" ? "web-main-section-hero" : undefined
                }
                alignment={this.layout === "centered" ? "center" : "start"}
                kicker={this.kicker}
                subtitle={this.subtitle}
              >
                {this.mainTitle}
              </qds-title>
              {(this.description ?? "") && (
                <p class="description">{this.description}</p>
              )}
            </div>
            <slot name="cta" />
          </div>
          <div class="extension" hidden={!this.hasExtensionContent}>
            <slot onSlotchange={this.handleSlotChange} />
          </div>
        </div>
      </section>
    );
  }

  private readonly handleSlotChange = (event: Event): void => {
    const slot = event.target as HTMLSlotElement;
    if (slot.name === "") {
      this.hasExtensionContent = slot.assignedElements().length > 0;
    }
  };
}
